/**
 * TODO: replace Terms and Privacy copy below with counsel-reviewed text before
 * public release. The structure matches what App Store and Play Store
 * submissions expect; the language is placeholder boilerplate authored for
 * Chancey MVP and has not been reviewed by an attorney.
 */

export interface FaqItem {
  q: string;
  a: string;
}

export interface FaqCategory {
  id: string;
  title: string;
  items: FaqItem[];
}

export interface LegalSection {
  heading: string;
  body: string[];
}

export interface LegalDocument {
  lastUpdated: string;
  intro: string;
  sections: LegalSection[];
}

export const FAQS: FaqCategory[] = [
  {
    id: 'scanning',
    title: 'Scanning tickets',
    items: [
      {
        q: 'How do I scan a lottery ticket?',
        a: 'Open Scan, then start the camera. Point at the printed numbers on your ticket and hold steady. Chancey identifies the game printed on the ticket, reads the numbers, and saves it to the right game history.',
      },
      {
        q: 'Chancey says my ticket is pending — what does that mean?',
        a: 'That draw has not happened yet, or the lottery has not posted results. We refresh on a schedule and update your ticket as soon as we can.',
      },
      {
        q: 'Can Chancey cash my winning ticket for me?',
        a: 'No. Chancey does not sell tickets or pay prizes. Take a winning ticket to an official lottery retailer or claims office.',
      },
      {
        q: 'The scanner is not reading my ticket. What should I do?',
        a: 'Try better light, hold the ticket flat, and avoid glare. If it still fails, use Enter manually on the Scan screen and type the numbers yourself.',
      },
    ],
  },
  {
    id: 'picks',
    title: 'Building picks',
    items: [
      {
        q: 'How does the number builder work?',
        a: 'The Builder tab suggests combos using simple ideas like how often numbers showed up in the past and a mix of high and low numbers. It helps you pick faster — it does not predict winners.',
      },
      {
        q: 'Do certain numbers really come up more often?',
        a: 'In a fair game every number has the same long-run odds. Short streaks are mostly luck. We show stats so you can pick numbers you like, not because a pattern guarantees a win.',
      },
    ],
  },
  {
    id: 'account',
    title: 'Account and data',
    items: [
      {
        q: 'How do I change my display name or email?',
        a: 'Go to Settings and tap Edit on your profile card to change your display name. Email changes are handled by the sign-in provider for your account.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Settings → Danger zone → Delete account disables your account and signs you out. If you sign in again later, the account reactivates with your saved tickets and preferences. For permanent data deletion, email lucky@chancey.io.',
      },
      {
        q: 'Where are my preferences stored?',
        a: 'We save them on your device and on your Chancey account so they follow you when you sign in on another phone.',
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    items: [
      {
        q: 'I turned on draw reminders but I am not getting any.',
        a: 'Push messages are not live yet. Your choices are saved on your account so we can turn them on later without you redoing everything.',
      },
      {
        q: 'How do I stop getting notifications?',
        a: 'Open Settings → Notifications and switch off what you do not want. You can also turn off Chancey alerts in your phone system settings.',
      },
    ],
  },
  {
    id: 'responsible',
    title: 'Responsible play',
    items: [
      {
        q: 'Is lottery play safe?',
        a: 'It can be fun in small doses but you can lose real money. If you spend more than feels okay, pause. Chancey is for info and fun — do not use rent or grocery money on tickets.',
      },
      {
        q: 'Where can I get help if I feel my play is a problem?',
        a: 'In the US, call or text 1-800-GAMBLER (1-800-426-2537) for free, private help. Elsewhere, search for your country’s problem-gambling helpline.',
      },
    ],
  },
];

export const TERMS: LegalDocument = {
  lastUpdated: 'April 23, 2026',
  intro:
    'These Terms of Service ("Terms") govern your use of the Chancey mobile application and related services (together, "Chancey" or the "Service"). By creating an account or using the Service you agree to these Terms.',
  sections: [
    {
      heading: '1. Eligibility',
      body: [
        'You must be at least 18 years old, or the age of majority in your jurisdiction, whichever is greater, to use Chancey. By using the Service you represent that you meet this requirement and that your use of Chancey does not violate any law applicable to you.',
        'Chancey verifies the 18+ requirement using the birthday on your account (either imported from the social provider you signed in with, or entered in Settings). If the birthday on your account indicates you are under 18, your access to the Service will be suspended and you can appeal by correcting the birthday or contacting lucky@chancey.io.',
      ],
    },
    {
      heading: '2. What Chancey is (and is not)',
      body: [
        'Chancey is an informational and convenience tool for lottery players. It helps you scan tickets you already own, view published draw results, and generate number suggestions. Chancey is not a lottery retailer and is not affiliated with, endorsed by, sponsored by, or operated by any state lottery, national lottery, government lottery agency, Mega Millions, Powerball, or the Multi-State Lottery Association. Chancey cannot sell tickets, cash prizes, or guarantee results.',
        'All winnings must be claimed through the official lottery operator that issued your ticket, subject to that operator\u2019s rules.',
      ],
    },
    {
      heading: '3. Your account',
      body: [
        'You are responsible for safeguarding your credentials and for all activity that occurs under your account. Notify us immediately at lucky@chancey.io if you believe your account has been compromised.',
        'You may not share, sell, or transfer your account, use the Service through automated means, or attempt to bypass any access or rate limits.',
      ],
    },
    {
      heading: '4. Subscriptions and billing',
      body: [
        'Chancey Pro Monthly is an auto-renewable subscription billed every month. Chancey Pro Annual is an auto-renewable subscription billed every year. Current prices are shown in the purchase sheet before you subscribe.',
        'Paid features, where offered, are billed through the platform you subscribed on (Apple App Store or Google Play) and are governed by that platform\u2019s billing terms in addition to these Terms. You can manage or cancel your subscription through your platform account settings at any time. Your subscription automatically renews unless it is canceled at least 24 hours before the end of the current billing period.',
        'Refunds are handled by the platform you paid through.',
      ],
    },
    {
      heading: '5. Acceptable use',
      body: [
        'You agree not to use Chancey to harass other users, submit false or infringing content, reverse engineer the Service, or interfere with its normal operation. We may suspend or terminate accounts that violate these rules.',
      ],
    },
    {
      heading: '6. Disclaimers',
      body: [
        'Chancey is provided on an "as is" and "as available" basis. We do not warrant that draw results, ticket scans, or number suggestions will always be accurate, uninterrupted, or error-free. Always verify important information, including winning status, with the official lottery operator.',
        'Number suggestions are for entertainment only and do not predict future draw outcomes.',
        'When on-device recognition is inconclusive, Chancey uses a third-party provider (OpenAI) to assist with reading ticket numbers. That provider\u2019s terms and policies also apply to the processing of your ticket image.',
      ],
    },
    {
      heading: '7. Limitation of liability',
      body: [
        'To the maximum extent permitted by law, Chancey and its operators are not liable for indirect, incidental, consequential, or punitive damages arising from your use of the Service, including any lost winnings, missed draws, or reliance on scanned or displayed information. Our total liability for any claim is limited to the amounts you paid us in the twelve months preceding the claim, or one hundred US dollars, whichever is greater.',
      ],
    },
    {
      heading: '8. Termination',
      body: [
        'You can stop using Chancey at any time by deleting your account from Settings. We may suspend or terminate access to the Service, with or without notice, for any breach of these Terms or if required by law. Sections that by their nature should survive termination will do so.',
      ],
    },
    {
      heading: '9. Changes to these Terms',
      body: [
        'We may update these Terms from time to time. If we make material changes we will notify you through the app or by email. Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms.',
      ],
    },
    {
      heading: '10. Governing law',
      body: [
        'These Terms are governed by the laws of the State of California, without regard to its conflict-of-laws principles. Any dispute will be resolved exclusively in the state or federal courts located in San Francisco County, California, unless applicable law requires otherwise.',
      ],
    },
    {
      heading: '11. Contact',
      body: [
        'Questions about these Terms? Reach us at lucky@chancey.io.',
      ],
    },
  ],
};

export const PRIVACY: LegalDocument = {
  lastUpdated: 'April 23, 2026',
  intro:
    'This Privacy Policy explains what information Chancey collects, how we use it, and the choices you have. We keep the list short on purpose: Chancey is designed to collect as little data as possible to make the app work.',
  sections: [
    {
      heading: '1. Information we collect',
      body: [
        'Account information: when you sign up we collect your name, email address, and any other identifiers you provide through our authentication provider (Clerk). Clerk stores your credentials; Chancey never sees your password.',
        'Birthday: we collect your birthday to verify the 18+ eligibility required by our Terms of Service and to personalise the app. If you sign in with Google we may, with your consent at the Google consent screen, request a birthday scope so we can pre-fill this field. You can decline on the Google screen and still use the app by entering it in Settings. Birthday can be cleared at any time from Settings > Birthday.',
        'Ticket scans: when you scan a ticket we process the numbers and draw date on the ticket to check results. Scanned ticket records are stored on your account so you can view your history. When on-device recognition is inconclusive, we send the captured ticket image to OpenAI for assisted recognition (see "Who we share information with" below). Images are processed only to return the ticket numbers and are not stored by Chancey.',
        'Preferences: your appearance, notification, and other in-app preferences are stored on your account so they sync across your devices.',
        'Operational logs: we log your Clerk user id alongside request metadata (route, timing, outcome, error code) for debugging, abuse prevention, and capacity planning. Logs are retained for up to 30 days and are not shared with third parties. No ticket images are logged.',
        'Device information: we collect basic technical details about your device (model, OS version, app version, and user-agent) when you contact support or when the app reports an error, so that we can reproduce and fix the problem.',
      ],
    },
    {
      heading: '2. How we use information',
      body: [
        'We use your information to provide and improve the Service, authenticate you, check ticket status, sync your preferences, respond to support requests, prevent abuse, and comply with legal obligations. We collect first-party product analytics and diagnostics such as app opens, screen views, feature outcomes, API errors, and client errors. Analytics events use anonymous install or hashed account identifiers and do not include ticket numbers, ticket photos, OCR text, emails, names, push tokens, subscription IDs, or full URLs. We do not sell your personal information.',
      ],
    },
    {
      heading: '3. Who we share information with',
      body: [
        'Authentication: Clerk (clerk.com) processes sign-in and stores your account credentials under its own privacy terms. If you sign in with Google we may request the `user.birthday.read` scope — you can decline at the consent screen and still use the app.',
        'Infrastructure: Cloudflare hosts the Chancey backend; it processes request metadata to run the service on our behalf.',
        'Assisted OCR: OpenAI (openai.com) receives ticket images when on-device recognition is inconclusive. Per OpenAI\u2019s API policy at time of writing, API data is not used to train their models. OpenAI may retain API inputs briefly per their default retention policy; we do not store those images on our side.',
        'Legal: we may disclose information if required by law or to protect the rights, property, or safety of users or the public.',
      ],
    },
    {
      heading: '4. Your rights and controls',
      body: [
        'You can view and edit your profile, export your data, or disable your account at any time from Settings > Danger zone > Delete account. Disabling signs you out and pauses account use; if you sign in again later, your saved tickets and preferences are restored. For permanent data deletion, email lucky@chancey.io.',
        'You can clear your birthday at any time from Settings > Birthday. If you clear it, access to the app is no longer gated by the 18+ check on the server side; our Terms of Service still apply.',
        'Depending on where you live, you may have additional rights under laws such as the GDPR or CCPA (access, correction, portability, deletion, objection). To exercise these rights, email lucky@chancey.io.',
      ],
    },
    {
      heading: '5. Data retention',
      body: [
        'Account and ticket data are retained while your account is active or disabled so your account can be reactivated if you sign in again. If you request permanent deletion by email, most data is removed within 30 days; anonymized aggregates may be retained for analytics.',
      ],
    },
    {
      heading: '6. Children',
      body: [
        'Chancey is intended for users 18 and older. We do not knowingly collect personal information from anyone under 18. If you believe a minor has created an account, contact lucky@chancey.io and we will delete it.',
      ],
    },
    {
      heading: '7. Security',
      body: [
        'We use industry-standard safeguards, including encryption in transit, to protect your information. No system is perfectly secure, but we work continuously to reduce risk.',
      ],
    },
    {
      heading: '8. Changes to this policy',
      body: [
        'If we make material changes we will notify you through the app or by email before the changes take effect. The "Last updated" date at the top of this page always reflects the current version.',
      ],
    },
    {
      heading: '9. Contact',
      body: [
        'Questions about this policy or about your data? Email lucky@chancey.io.',
      ],
    },
  ],
};
